import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, PackagePlus, Trash2, PencilLine } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import InventoryStatusBadge from "@/components/inventory/InventoryStatusBadge";
import EditItemDialog from "@/components/inventory/EditItemDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";

export default function ItemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState(null); // { type: 'adjustment'|'wastage'|'expired', batch }
  const [formQty, setFormQty] = useState("");
  const [formReason, setFormReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const fetchDetail = useCallback(async () => {
    const { data } = await api.get(`/inventory/items/${id}`);
    setData(data);
    const { data: audit } = await api.get(`/inventory/items/${id}/audit-log`);
    setAuditLog(audit);
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const openDialog = (type, batch) => {
    setDialog({ type, batch });
    setFormQty("");
    setFormReason("");
  };

  const handleSubmitDialog = async () => {
    if (!dialog) return;
    setSubmitting(true);
    try {
      if (dialog.type === "adjustment") {
        await api.post(`/inventory/batches/${dialog.batch.id}/adjustment`, { quantity_delta: Number(formQty), reason: formReason });
      } else {
        await api.post(`/inventory/batches/${dialog.batch.id}/mark`, { type: dialog.type, quantity: Number(formQty), reason: formReason });
      }
      toast({ title: "Saved" });
      setDialog(null);
      fetchDetail();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <StaffHeader />
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      </div>
    );
  }

  const { item, batches, movements, usage_last_6_months } = data;
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        <InventorySubNav />

        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="font-display text-3xl text-amtm-text" data-testid="item-detail-name">{item.name}</h1>
            <p className="text-amtm-textMuted">{item.generic_name ? `${item.generic_name} · ` : ""}{item.company_name || "No company listed"} · {item.category_name} · {item.location_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <InventoryStatusBadge status={item.status} testId="item-detail-status" />
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="item-detail-edit-button">
                <PencilLine className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4">
            <p className="text-xl font-bold text-amtm-text" data-testid="item-detail-total-stock">{item.total_stock} {item.unit}</p>
            <p className="text-xs text-amtm-textMuted">Total stock</p>
          </div>
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4">
            <p className="text-xl font-bold text-amtm-text">{item.usable_stock} {item.unit}</p>
            <p className="text-xs text-amtm-textMuted">Usable (non-expired)</p>
          </div>
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4">
            <p className="text-xl font-bold text-amtm-text">{item.earliest_expiry || "—"}</p>
            <p className="text-xs text-amtm-textMuted">Earliest expiry</p>
          </div>
        </div>

        <h2 className="font-semibold text-amtm-text mb-2">Batches</h2>
        <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border mb-6" data-testid="item-detail-batches-list">
          {batches.map((b) => (
            <div key={b.id} className="flex items-center justify-between p-3 gap-3 flex-wrap" data-testid={`batch-row-${b.id}`}>
              <div className="text-sm">
                <p className="font-medium text-amtm-text">Exp {b.expiry_label} · {b.quantity_remaining} / {b.quantity_received} {item.unit}</p>
                <p className="text-xs text-amtm-textMuted">{b.batch_number ? `Batch ${b.batch_number} · ` : ""}Added {formatDate(b.date_added)} by {b.added_by_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <InventoryStatusBadge status={b.status} testId={`batch-status-${b.id}`} />
                {isAdmin && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openDialog("adjustment", b)} data-testid={`batch-adjust-button-${b.id}`}><PencilLine className="h-3.5 w-3.5 mr-1" /> Adjust</Button>
                    {b.status === "expired" && b.quantity_remaining > 0 && (
                      <Button size="sm" variant="outline" onClick={() => openDialog("expired", b)} data-testid={`batch-mark-expired-button-${b.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Mark Expired</Button>
                    )}
                    {b.quantity_remaining > 0 && (
                      <Button size="sm" variant="outline" onClick={() => openDialog("wastage", b)} data-testid={`batch-mark-wastage-button-${b.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Wastage</Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-semibold text-amtm-text mb-2">Usage — last 6 months</h2>
        <div className="flex gap-2 mb-6 flex-wrap">
          {usage_last_6_months.length === 0 ? (
            <p className="text-sm text-amtm-textMuted">No usage recorded yet.</p>
          ) : (
            usage_last_6_months.map((u) => (
              <div key={u.month} className="bg-amtm-surface border border-amtm-border rounded-lg px-3 py-2 text-center" data-testid={`usage-month-${u.month}`}>
                <p className="font-semibold text-amtm-text">{u.quantity}</p>
                <p className="text-xs text-amtm-textMuted">{u.month}</p>
              </div>
            ))
          )}
        </div>

        <h2 className="font-semibold text-amtm-text mb-2">Movement history</h2>
        <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border mb-6" data-testid="item-detail-movements-list">
          {movements.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-3 gap-3 flex-wrap text-sm" data-testid={`movement-row-${m.id}`}>
              <div>
                <p className="font-medium text-amtm-text capitalize">{m.type} — {m.quantity} {item.unit}</p>
                <p className="text-xs text-amtm-textMuted">{formatDate(m.date)} by {m.staff_name}{m.reason ? ` — ${m.reason}` : ""}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-semibold text-amtm-text mb-2">Edit history</h2>
        <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="item-detail-audit-list">
          {auditLog.length === 0 ? (
            <p className="text-sm text-amtm-textMuted p-3">No edits recorded yet.</p>
          ) : (
            auditLog.map((entry) => (
              <div key={entry.id} className="p-3 text-sm" data-testid={`audit-row-${entry.id}`}>
                <p className="text-xs text-amtm-textMuted mb-1">{formatDate(entry.changed_at)} by {entry.changed_by_name}</p>
                {entry.changes.map((c, i) => (
                  <p key={i} className="text-amtm-text">{c.field}: <span className="text-amtm-textMuted">{String(c.from)}</span> → <span className="font-medium">{String(c.to)}</span></p>
                ))}
              </div>
            ))
          )}
        </div>

        <EditItemDialog itemId={id} open={editing} onOpenChange={setEditing} onSaved={fetchDetail} />

        <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
          <DialogContent data-testid="batch-action-dialog">
            <DialogHeader>
              <DialogTitle>
                {dialog?.type === "adjustment" ? "Record an adjustment" : dialog?.type === "expired" ? "Mark batch as expired" : "Record wastage"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{dialog?.type === "adjustment" ? "Quantity change (use a negative number to reduce)" : "Quantity"}</Label>
                <Input type="number" step="any" value={formQty} onChange={(e) => setFormQty(e.target.value)} className="mt-1.5" data-testid="batch-dialog-qty-input" />
              </div>
              <div>
                <Label>Reason {dialog?.type === "adjustment" ? "(required)" : "(optional)"}</Label>
                <Textarea value={formReason} onChange={(e) => setFormReason(e.target.value)} className="mt-1.5" data-testid="batch-dialog-reason-input" />
              </div>
              <Button
                onClick={handleSubmitDialog}
                disabled={submitting || !formQty || (dialog?.type === "adjustment" && formReason.trim().length < 3)}
                data-testid="batch-dialog-submit-button"
                className="bg-amtm-teal text-white hover:bg-amtm-tealHover"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackagePlus className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
